import React, { useState, useEffect } from "react";
import TabGPT from "../tabs/TabGPT";
import TextField2T from "../textfields/TextField2T";
import DropDownGPT03 from "../dropdowns/DropDownGTP03";
import Space from "../layout/Space";
import { queryCollection } from "../models/DataFunctions";
import SmallSpinner from "../loading/SmallSpinner";

interface ConstructorSectionProps {
  orgId: string;
  onConstructorChange: (constructorData: any | null) => void;
}

function ConstructorSection({ orgId, onConstructorChange }: ConstructorSectionProps) {
  const [activeTab, setActiveTab] = useState("nouveau");
  const [loading, setLoading] = useState(true);
  const [partnerFiches, setPartnerFiches] = useState<any[]>([]);

  // Nouveau Constructeur form fields
  const [entreprise, setEntreprise] = useState("");
  const [email, setEmail] = useState("");
  const [tel, setTel] = useState("");
  const [city, setCity] = useState("");
  const [adresse, setAdresse] = useState("");
  const [codePostal, setCodePostal] = useState("");

  // Selected fiche from dropdown
  const [selectedFiche, setSelectedFiche] = useState<any>(null);

  const tabs = [
    { label: "Nouveau Constructeur", id: "nouveau" },
    { label: "Mes fiches", id: "fiches" },
  ];

  useEffect(() => {
    fetchPartnerFiches();
  }, [orgId]);

  useEffect(() => {
    // Update parent component when constructor data changes
    // Map fields to match backend expectations: name, address, postal, city, phone, email
    if (activeTab === "nouveau") {
      if (entreprise.trim()) {
        onConstructorChange({
          name: entreprise.trim(),
          email: email.trim(),
          phone: tel.trim(),
          city: city.trim(),
          address: adresse.trim(),
          postal: codePostal.trim(),
        });
      } else {
        onConstructorChange(null);
      }
    } else {
      if (selectedFiche) {
        onConstructorChange({
          name: selectedFiche.entreprise || "",
          email: selectedFiche.email || "",
          phone: selectedFiche.tel || "",
          city: selectedFiche.city || "",
          address: selectedFiche.adresse || "",
          postal: selectedFiche.code_postal || "",
        });
      } else {
        onConstructorChange(null);
      }
    }
  }, [activeTab, entreprise, email, tel, city, adresse, codePostal, selectedFiche]);

  const fetchPartnerFiches = async () => {
    setLoading(true);
    try {
      const filters = [
        { field: "org_id", operator: "==", value: orgId },
        { field: "type", operator: "==", value: "bookshelf_partner" }
      ];
      const fiches = await queryCollection("logement_bookshelf", filters);
      setPartnerFiches(fiches || []);
    } catch (error) {
      console.error("Error fetching partner fiches:", error);
      setPartnerFiches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFicheSelect = (option: any) => {
    setSelectedFiche(option);
  };

  const renderTabContent = () => {
    if (activeTab === "nouveau") {
      return (
        <div className="flex flex-col gap-3">
          <TextField2T
            label="Nom de l'entreprise"
            value={entreprise}
            onChange={(e) => setEntreprise(e.target.value)}
          />
          <TextField2T
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField2T
            label="Téléphone"
            value={tel}
            onChange={(e) => setTel(e.target.value)}
          />
          <TextField2T
            label="Adresse"
            value={adresse}
            onChange={(e) => setAdresse(e.target.value)}
          />
          <TextField2T
            label="Ville"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
          <TextField2T
            label="Code Postal"
            value={codePostal}
            onChange={(e) => setCodePostal(e.target.value)}
          />
        </div>
      );
    } else {
      if (loading) {
        return (
          <div className="flex items-center justify-center py-8">
            <SmallSpinner />
          </div>
        );
      }

      const ficheOptions = partnerFiches.map(fiche => ({
        value: fiche.id,
        label: `${fiche.entreprise || 'Sans nom'} ${fiche.city ? `- ${fiche.city}` : ''}`.trim(),
        ...fiche
      }));

      return (
        <div className="flex flex-col gap-3">
          <DropDownGPT03
            value={selectedFiche?.value}
            label="Sélectionner une fiche partenaire"
            items={ficheOptions}
            onChange={handleFicheSelect}
          />
          {selectedFiche && (
            <div className="bg-gray-50 p-4 rounded-lg mt-2">
              <div className="text-h3 font-semibold mb-2">{selectedFiche.entreprise}</div>
              <div className="text-h4 text-gray-600 space-y-1">
                {selectedFiche.email && <div>Email: {selectedFiche.email}</div>}
                {selectedFiche.tel && <div>Téléphone: {selectedFiche.tel}</div>}
                {selectedFiche.adresse && <div>Adresse: {selectedFiche.adresse}</div>}
                {selectedFiche.city && <div>Ville: {selectedFiche.city}</div>}
                {selectedFiche.code_postal && <div>Code Postal: {selectedFiche.code_postal}</div>}
              </div>
            </div>
          )}
          {!loading && ficheOptions.length === 0 && (
            <div className="text-h4 text-gray-500 py-4 text-center">
              Aucune fiche partenaire disponible. Créez-en une dans la Bibliothèque.
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="text-h2 font-semibold">Constructeur (optionnel)</div>
      <TabGPT
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <div className="mt-2">
        {renderTabContent()}
      </div>
    </div>
  );
}

export default ConstructorSection;
